import React, { useState } from 'react';
import { Button } from '../ui/button';
import {
  MousePointer,
  Undo2,
  Redo2,
  Trash2,
  Square,
  Check,
  Loader2,
  Layers,
  RefreshCcw,
  X,
  Sparkles
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { QuickReplacePopover } from './QuickReplacePopover';
import { ElementSuggestionMetadata } from '../../types/elementSuggestion';

interface GsapEditToolbarProps {
  selectedCount: number;
  canUndo: boolean;
  canRedo: boolean;
  isSaving?: boolean; // Loading state for Done button
  isAssetSelected?: boolean; // Whether a replaceable asset is selected
  isReplacing?: boolean; // Loading state for Replace button
  onSelectTool: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onDelete: () => void;
  onGroup: () => void;
  onDeselect: () => void;
  onDone: () => void;
  onExit: () => void; // Handler for Exit button (discard changes)
  onReplace?: () => void; // Handler for Replace button (opens full modal)
  onQuickReplace?: (url: string, source: string) => void; // Handler for quick replacement from popover
  suggestionMetadata?: ElementSuggestionMetadata | null; // AI-assisted search suggestions
  className?: string;
}

export const GsapEditToolbar: React.FC<GsapEditToolbarProps> = ({
  selectedCount,
  canUndo,
  canRedo,
  isSaving = false,
  isAssetSelected = false,
  isReplacing = false,
  onSelectTool,
  onUndo,
  onRedo,
  onDelete,
  onGroup,
  onDeselect,
  onDone,
  onExit,
  onReplace,
  onQuickReplace,
  suggestionMetadata,
  className = '',
}) => {
  // State for controlling the QuickReplacePopover
  const [showQuickReplace, setShowQuickReplace] = useState(false);

  // Determine if we should show the quick replace popover (has search terms)
  const hasSearchSuggestions = suggestionMetadata?.searchTerms && suggestionMetadata.searchTerms.length > 0;
  return (
    <div className={cn(
      "flex items-center gap-2 px-2",
      className
    )}>
      {/* Tool Group */}
      <div className="flex items-center gap-1">
        <Button
          onClick={onSelectTool}
          variant="ghost"
          size="sm"
          className="transition-all"
          title="Select Elements"
        >
          <MousePointer className="h-4 w-4" />
        </Button>
      </div>

      <div className="w-px h-6 bg-border/50" />

      {/* Action Group */}
      <div className="flex items-center gap-1">
        <Button
          onClick={onUndo}
          variant="ghost"
          size="sm"
          disabled={!canUndo}
          className="transition-all disabled:opacity-30"
          title="Undo"
        >
          <Undo2 className="h-4 w-4" />
        </Button>
        <Button
          onClick={onRedo}
          variant="ghost"
          size="sm"
          disabled={!canRedo}
          className="transition-all disabled:opacity-30"
          title="Redo"
        >
          <Redo2 className="h-4 w-4" />
        </Button>
      </div>

      <div className="w-px h-6 bg-border/50" />

      {/* Edit Group */}
      <div className="flex items-center gap-1">
        <Button
          onClick={onDelete}
          variant="ghost"
          size="sm"
          disabled={selectedCount === 0}
          className="transition-all disabled:opacity-30"
          title="Delete Selected"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
        <Button
          onClick={onGroup}
          variant="ghost"
          size="sm"
          disabled={selectedCount < 2}
          className="transition-all disabled:opacity-30"
          title="Group Selected Elements (Cmd+G)"
        >
          <Layers className="h-4 w-4" />
        </Button>
        {/* Replace button - only shown when a replaceable asset is selected */}
        {(isAssetSelected || isReplacing) && onReplace && (
          hasSearchSuggestions && onQuickReplace ? (
            // Wrap with QuickReplacePopover when search suggestions are available
            <QuickReplacePopover
              isOpen={showQuickReplace}
              onOpenChange={setShowQuickReplace}
              suggestionMetadata={suggestionMetadata || null}
              onQuickReplace={onQuickReplace}
              onOpenFullSearch={() => {
                setShowQuickReplace(false);
                onReplace();
              }}
            >
              <Button
                variant="ghost"
                size="sm"
                disabled={isReplacing}
                className={cn(
                  "transition-all",
                  isReplacing
                    ? "text-primary cursor-wait"
                    : "text-primary hover:text-accent-foreground hover:bg-accent"
                )}
                title={isReplacing ? "Replacing..." : "Replace (with AI suggestions)"}
              >
                <RefreshCcw className={cn("h-4 w-4 mr-1", isReplacing && "animate-spin")} />
                {isReplacing ? "Replacing..." : "Replace"}
                <Sparkles className="h-3 w-3 ml-1 text-amber-500" />
              </Button>
            </QuickReplacePopover>
          ) : (
            // Standard Replace button without popover
            <Button
              onClick={onReplace}
              variant="ghost"
              size="sm"
              disabled={isReplacing}
              className={cn(
                "transition-all",
                isReplacing
                  ? "text-primary cursor-wait"
                  : "text-primary hover:text-accent-foreground hover:bg-accent"
              )}
              title={isReplacing ? "Replacing..." : "Replace Image/Video"}
            >
              <RefreshCcw className={cn("h-4 w-4 mr-1", isReplacing && "animate-spin")} />
              {isReplacing ? "Replacing..." : "Replace"}
            </Button>
          )
        )}
        <Button
          onClick={onDeselect}
          variant="ghost"
          size="sm"
          disabled={selectedCount === 0}
          className="transition-all disabled:opacity-30"
          title="Deselect All"
        >
          <Square className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex-1" />

      {/* Done Group */}
      <div className="flex items-center gap-2">
        {selectedCount > 0 && (
          <span className="text-sm text-muted-foreground">
            {selectedCount} selected
          </span>
        )}
        <Button
          onClick={onDone}
          variant="default"
          size="sm"
          disabled={isSaving}
          className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
          title={isSaving ? "Saving changes..." : "Done Editing"}
        >
          {isSaving ? (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <Check className="h-4 w-4 mr-1" />
          )}
          {isSaving ? 'Saving...' : 'Done'}
        </Button>
      </div>

      {/* Exit Button - Far Right */}
      <Button
        onClick={onExit}
        variant="outline"
        size="sm"
        disabled={isSaving}
        className="text-muted-foreground hover:text-destructive hover:border-destructive disabled:opacity-50 ml-4"
        title="Exit without saving"
      >
        <X className="h-4 w-4 mr-1" />
        Exit
      </Button>
    </div>
  );
};